<template>
    <b-container fluid style="margin-top: 60px">
        <h1 v-on:click="getData">
            {{ strProductTitle }}
            <b-button
                @click="getData()"
                class="bgc_aux_normal"
                v-b-tooltip.hover
                :title="$t('com_refresh', { ns: 'common' })"
                ><font-awesome-icon icon="arrow-down" size="1x" />
            </b-button>
        </h1>
        <b-row>
            <b-col sm="12">
                <b-row no-gutters>
                    <b-col lg="6" class="my-1">
                        <b-form-group
                            :label="$t('com_filter', { ns: 'common' })"
                            label-for="filter-input"
                            label-cols-sm="3"
                            label-align-sm="right"
                            label-size="sm"
                            class="mb-0"
                        >
                            <b-input-group size="sm">
                                <b-form-input
                                    id="filter-input"
                                    v-model="filter"
                                    type="search"
                                    :placeholder="
                                        $t('com_typetosearch', { ns: 'common' })
                                    "
                                ></b-form-input>

                                <b-input-group-append>
                                    <b-button
                                        :disabled="!filter"
                                        @click="filter = ''"
                                        >{{
                                            $t('com_wis', { ns: 'common' })
                                        }}</b-button
                                    >
                                </b-input-group-append>
                            </b-input-group>

                            <b-button
                                size="sm"
                                @click="createNewItem"
                                class="mr-1"
                                ><font-awesome-icon
                                    icon="folder-plus"
                                    size="1x"
                                />
                                {{ strSimpleProduct }}
                            </b-button>
                        </b-form-group>
                    </b-col>

                    <b-col lg="6" class="my-1">
                        <b-form-group
                            :label="$t('com_perpagina', { ns: 'common' })"
                            label-for="per-page-select"
                            label-cols-sm="6"
                            label-cols-md="4"
                            label-cols-lg="3"
                            label-align-sm="right"
                            label-size="sm"
                            class="mb-0"
                        >
                            <b-form-select
                                id="per-page-select"
                                v-model="perPage"
                                :options="pageOptions"
                                size="sm"
                            ></b-form-select>
                            <b-pagination
                                v-model="currentPage"
                                :total-rows="totalRows"
                                :per-page="perPage"
                                align="fill"
                                size="sm"
                                class="my-0"
                            ></b-pagination>
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-table
                    :items="displayList"
                    :fields="visibleFields"
                    :current-page="currentPage"
                    :per-page="perPage"
                    :filter="filter"
                    :filter-included-fields="filterOn"
                    stacked="md"
                    show-empty
                    :empty-text="$t('com_geen_data', { ns: 'common' })"
                    small
                    striped
                    @filtered="onFiltered"
                    @row-clicked="toonVoorraad"
                >
                    <template #cell(actions)="row">
                        <b-button
                            size="sm"
                            v-if="useVersion10"
                            @click="editItem(row.item)"
                            class="bgc_aux_warning"
                            :disabled="systemProduct(row.item)"
                            ><font-awesome-icon icon="edit" size="1x" />
                        </b-button>
                        <b-button
                            v-if="checkRight('ProductDelete')"
                            size="sm"
                            @click="verwijderItem(row.item)"
                            :disabled="systemProduct(row.item)"
                            class="bgc_aux_alarm"
                            ><font-awesome-icon icon="trash-alt" size="1x" />
                        </b-button>
                        <b-button
                            size="sm"
                            @click="toonVoorraad(row.item)"
                            class="bgc_aux_unknown"
                            ><font-awesome-icon icon="info-circle" size="1x" />
                        </b-button>
                    </template>

                    <template #row-details>
                        <stock-items v-bind:stocklist="stockList">
                        </stock-items>
                    </template>
                </b-table>
            </b-col>
        </b-row>

        <plus-product-modal
            v-if="detailsVisible"
            :inItem="currentItem"
            v-model="detailsVisible"
            @hide="reset"
        />
    </b-container>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { VueExtension } from '../../models/VueExtension';
import { loadItem, loadList, loadGetItem } from '../../models/DataHelper';
import { Feature } from '../../models/Features';
import { Permission } from '../../models/Permissions';
import { Product } from '../../models/Product';
import { VoorraadItem } from '../../models/Voorraad';
import PlusProductModal from './PlusProductModal.vue';
import StockItems from '../stock/StockItems.vue';

export default defineComponent({
    name: 'PlusProductBase',
    data: function () {
        return {
            productLijst: [] as Array<Product>,
            stockList: [] as Array<VoorraadItem>,
            totalRows: 1,
            currentPage: 1,
            perPage: 20,
            pageOptions: [10, 20, 40, { value: 100, text: '100' }],
            filter: null,
            filterOn: [],
            currentItem: {} as Product, // || null,
            detailsVisible: false
        };
    },
    props: {},
    components: {
        StockItems,
        PlusProductModal
    },
    computed: {
        strProductTitle() {
            return this.$t('tien_title', {
                ns: 'tien',
                val: this.$t('com_product', { ns: 'common' })
            });
        },
        strSimpleProduct() {
            return this.$t('com_newx', {
                ns: 'common',
                val: this.$t('tien_title', {
                    ns: 'tien',
                    val: this.$t('com_product', { ns: 'common' })
                })
            });
        },
        strDeleteProduct() {
            return this.$t('com_delete', {
                ns: 'common',
                val: this.$t('com_product', { ns: 'common' })
            });
        },
        displayList() {
            return this.productLijst;
        },
        useVersion10() {
            return this.checkFeature('conf_CH10Enabled');
        },
        visibleFields() {
            const fieldsInit = [
                { key: 'actions', label: ' ', visible: true },
                { key: 'id', label: 'Id', sortable: false, visible: false },
                {
                    key: 'naam',
                    label: this.$t('com_naam', { ns: 'common' }),
                    sortable: true,
                    visible: true
                },
                {
                    key: 'internID',
                    label: this.$t('com_internalid', { ns: 'common' }),
                    sortable: true,
                    visible: true
                }
            ];
            return fieldsInit.filter((field) => field.visible);
        }
    },
    methods: {
        systemProduct(item) {
            const isSystem = item.id < 1000;
            return isSystem;
        },
        createNewItem() {
            this.currentItem = new Product({});
            this.detailsVisible = true;
        },
        async editItem(item) {
            // haal item op met details
            const selData = { productID: item.id };
            this.currentItem = await loadItem(
                'product/loadProduct',
                selData,
                this.$t
            );
            this.detailsVisible = true;
        },
        async verwijderItem(item) {
            const doClose = await (
                this.$root as VueExtension
            ).showConfirmationBox(
                this.strDeleteProduct + item.id + ' : ' + item.naam
            );
            if (doClose) {
                loadItem(
                    'product/deleteProduct',
                    {
                        item: item,
                        prodcat: null
                    },
                    this.$t
                );
            }
        },
        async toonVoorraad(row) {
            if (row._showDetails) {
                this.$set(row, '_showDetails', false);
            } else {
                this.displayList.forEach((row) => {
                    this.$set(row, '_showDetails', false);
                });
                this.$set(row, '_showDetails', true);
                const selectionData = { productid: row.id };
                this.stockList = await loadList(
                    'product/getStockItemLijst',
                    'product/loadStockByProduct',
                    selectionData,
                    true,
                    this.$t
                );
            }
        },
        reset() {
            this.getData();
        },
        onFiltered(filteredItems) {
            // Trigger pagination to update the number of buttons/pages due to filtering
            this.totalRows = filteredItems.length;
            this.currentPage = 1;
        },
        async getData() {
            this.productLijst = await loadList(
                'tienplus/getSimpleProductLijst',
                'tienplus/loadSimpleProductInfo',
                null,
                true,
                this.$t
            );
            this.totalRows = this.productLijst ? this.productLijst.length : 0;
        },
        checkFeature(feature) {
            const key = Feature.enum(feature);
            return (this.$root as VueExtension).handleFeature(key);
        },
        checkRight(recht) {
            const id = Permission.enum(recht);
            return (this.$root as VueExtension).handleRight(id);
        }
    },
    async mounted() {
        await this.getData();
    }
});
</script>

<style scoped></style>

